<template>
  <my-echarts ref="chartRef" :option="option" :style="{ width: props.width, height: props.height }"></my-echarts>
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue";

const chartRef = ref();

const props = defineProps({
  // 数据源
  dataSource: {
    type: Array,
    default: () => [],
  },
  // 图表宽度
  width: {
    type: String,
    default: "100%",
  },
  // 图表高度
  height: {
    type: String,
    default: "100%",
  },
  // 提示框配置
  tooltip: {
    type: Object,
    default: () => ({})
  },
  // 图例配置
  legend: {
    type: Object,
    default: () => ({})
  },
  // 环形图半径配置
  radius: {
    type: Array,
    default: () => ['50%', '75%']
  },
  // 环形图中心点位置
  center: {
    type: Array,
    default: () => ['40%', '55%']
  },
  // 颜色列表
  colorList: {
    type: Array,
    default: () => ['#597ef7', '#40a9ff', '#36cfc9', '#9254de', '#f759ab', '#ff4d4f', '#ff7a45'],
  },
  fieldNames: {
    type: Object,
    default: () => ({ name: 'name', value: 'value' })
  },
  // 图表标题
  title: {
    type: String,
    default: ''
  }
});

const option = ref({});

// 计算颜色列表
const computedColorList = computed(() => {
  return props.colorList;
});

// 初始化图表配置
const initData = () => {
  // 如果没有数据，则清空图表
  if (!props.dataSource || props.dataSource.length === 0) {
    option.value = {
      title: {
        text: '暂无数据',
        x: 'center',
        y: 'center'
      }
    };
    return;
  }
  // 处理数据
  const processedData = props.dataSource.map(item => {
    return {
      name: item[props.fieldNames.name],
      value: item[props.fieldNames.value]
    };
  });

  option.value = {
    color: computedColorList.value,
    title: {
      text: props.title,
      left: '25%',
      top: 'top'
    },
    tooltip: {
      trigger: 'item',
      ...props.tooltip
    },
    legend: {
      show: true,
      top: 'middle',
      left: '70%',
      icon: 'circle',
      orient: 'vertical',
      formatter: (name) => {
        // 查找对应的数据项
        const dataItem = processedData.find(item => item.name === name);
        // 返回 name:value 格式的字符串
        return `${name}  ${dataItem ? dataItem.value : ''}`;
      },
      ...props.legend
    },
    series: [
      {
        type: 'pie',
        radius: props.radius,
        center: props.center,
        silent: props.silent,
        data: processedData,
        label: {
          show: false,
          position: 'outside',
          ...props.label
        },
        labelLine: {
          show: false,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
};

// 监听数据源变化
watch(
  () => [props.dataSource, props.radius, props.center, props.colorList, props.title],
  () => {
    nextTick(() => {
      initData();
    })
  },
  { immediate: true, deep: true }
);

// 暴露option和chart实例给父组件使用
defineExpose({
  option,
  resize: () => {
    if (chartRef.value && chartRef.value.chart) {
      chartRef.value.chart.resize();
    }
  },
  reDrawer: () => {
    if (chartRef.value && chartRef.value.reDrawer) {
      chartRef.value.reDrawer();
    }
  },
  chartInstance: chartRef
});
</script>